import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Phone, Mail, MapPin, Send, Globe } from 'lucide-react';
import Logo from './Logo';
import { useApp } from '../context/AppContext';
import { areas as fallbackAreas } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';

const Footer = () => {
  const { t, settings, developers } = useApp();
  const companyInfo = settings;
  const areas = fallbackAreas;
  const rawWebsite = (companyInfo.website || 'www.gobracapital.ae').trim();
  const websiteHref = /^https?:\/\//i.test(rawWebsite) ? rawWebsite : `https://${rawWebsite.replace(/^\/+/, '')}`;
  const websiteLabel = rawWebsite.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return (
    <footer className="bg-gobra-slate text-white/80">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <Logo variant="light" />
          <p className="text-sm mt-4 leading-relaxed text-white/60">{t('footer_tagline')}</p>
          <div className="flex items-center gap-3 mt-5">
            <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer"
              className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gobra-terracotta transition-colors" aria-label="Instagram">
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-title text-white font-semibold mb-4">{t('nav_developers')}</h4>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {developers.map((d) => (
              <li key={d.id}><Link to={`/projects?developer=${d.id}`} className="hover:text-gobra-orange transition-colors">{d.name}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-title text-white font-semibold mb-4">{t('nav_areas')}</h4>
          <ul className="space-y-2 text-sm">
            {areas.slice(0, 7).map((a) => (
              <li key={a}><Link to={`/areas/${encodeURIComponent(a)}`} className="hover:text-gobra-orange transition-colors">{a}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-title text-white font-semibold mb-4">{t('newsletter')}</h4>
          <p className="text-sm text-white/60 mb-3">{t('newsletter_desc')}</p>
          <div className="flex gap-2">
            <Input placeholder={t('email')} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" />
            <Button className="bg-gobra-terracotta hover:bg-gobra-orange text-white shrink-0"><Send className="h-4 w-4" /></Button>
          </div>
          <ul className="space-y-3 text-sm mt-6">
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-gobra-orange" /> {companyInfo.phone}</li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-gobra-orange" /> {companyInfo.email}</li>
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gobra-orange" /> {companyInfo.address}</li>
            <li className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-gobra-orange" />
              <a href={websiteHref} target="_blank" rel="noopener noreferrer" data-testid="footer-website-contact" className="hover:text-gobra-orange transition-colors">{websiteLabel}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        {companyInfo.footerLicense && (
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-5 text-center md:text-left text-xs text-white/40" data-testid="footer-license">
            {companyInfo.footerLicense}
          </div>
        )}
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <span>&copy; {new Date().getFullYear()} {companyInfo.name}. {t('rights')}</span>
          <div className="flex items-center gap-3">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-gobra-orange transition-colors">Privacy Policy</Link>
            <span className="text-white/30">&middot;</span>
            <a href={websiteHref} target="_blank" rel="noopener noreferrer" data-testid="footer-website-link" className="text-gobra-orange hover:text-white transition-colors font-medium">{websiteLabel}</a>
            <span className="text-white/30">&middot;</span>
            <span>Dubai &middot; UAE</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
